import { Injectable, NgZone } from '@angular/core';

import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { MapsAPILoader } from '@duxor/agm-core';
import { ConfigService, CustomErrorType } from '../../../core/public_api';
import { GoogleMapsAddressType } from '../../entities/google-maps-address-type.entity';
import { MHLocation } from '../../entities/mh-location.entity';

@Injectable({
    providedIn: 'root'
})
export class ShGoogleMapsService {
    private geocoder: google.maps.Geocoder;
    private readonly DEFAULT_ZOOM_LEVEL = 14;

    // GeoCode API requires at least 4 characters to return any results
    private readonly INPUT_MIN_CHARS: number = 4;

    private readonly belgiumBounds: google.maps.LatLngBoundsLiteral = {
        north: 52,
        south: 49,
        west: 2.49,
        east: 6.5
    };

    constructor(
        private readonly ngZone: NgZone,
        private readonly configService: ConfigService<any>,
        private readonly mapsApiLoader: MapsAPILoader
    ) {}

    private static mapGeocoderResultToMHLocation(location: google.maps.GeocoderResult): MHLocation {
        const mhLocation: MHLocation = {
            Location: location.geometry.location.lat() + ', ' + location.geometry.location.lng(),
            Address: location.formatted_address,
            Country: ShGoogleMapsService.getAddressComponentByType(location.address_components, ['country']),
            Area: ShGoogleMapsService.getAddressComponentByType(location.address_components, [
                'administrative_area_level_1'
            ]),
            SubArea: ShGoogleMapsService.getAddressComponentByType(location.address_components, [
                'administrative_area_level_2'
            ]),
            Postal: ShGoogleMapsService.getAddressComponentByType(location.address_components, ['postal_code']),
            Locality: ShGoogleMapsService.getAddressComponentByType(location.address_components, ['locality']),
            SubLocality: ShGoogleMapsService.getAddressComponentByType(location.address_components, ['sublocality']),
            Street: ShGoogleMapsService.getAddressComponentByType(location.address_components, [
                'street_address',
                'route'
            ]),
            Number: ShGoogleMapsService.getAddressComponentByType(location.address_components, ['street_number'])
        };
        return mhLocation;
    }

    private static getAddressComponentByType(
        addressComponents: google.maps.GeocoderAddressComponent[],
        types: GoogleMapsAddressType[]
    ): string {
        const component: google.maps.GeocoderAddressComponent | undefined = addressComponents.find(record =>
            record.types.some(type => types.includes(type as GoogleMapsAddressType))
        );

        if (typeof component === 'undefined') {
            return '';
        }

        return component.long_name;
    }

    getClientZoomLevel(): Observable<number> {
        return this.configService
            .getConfig()
            .pipe(map(config => config.defaultMapView?.zoomLevel ?? this.DEFAULT_ZOOM_LEVEL));
    }

    getClientLocation(): Observable<google.maps.LatLngLiteral | null> {
        return this.configService.getConfig().pipe(map(config => config.defaultMapView?.clientCoordinate ?? null));
    }

    publicLoadApi(): Observable<boolean> {
        return from(this.mapsApiLoader.load()).pipe(map(() => true));
    }

    private loadMapsAPI(): Observable<boolean> {
        if (!this.geocoder) {
            return from(this.mapsApiLoader.load()).pipe(
                tap(() => this.initGeocoder()),
                map(() => true)
            );
        }
        return of(true);
    }

    private initGeocoder(): void {
        this.geocoder = new google.maps.Geocoder();
    }

    searchByAddress(searchAddress: string): Observable<MHLocation | null> {
        const trimmedSearchAddress: string = searchAddress.trim();
        if (!trimmedSearchAddress || trimmedSearchAddress.length < this.INPUT_MIN_CHARS) {
            return of(null);
        }

        const geocoderRequest: google.maps.GeocoderRequest = {
            address: trimmedSearchAddress,
            bounds: this.belgiumBounds
        };
        return this.loadMapsAPI().pipe(
            switchMap(() => this.makeGeocoderRequest(geocoderRequest)),
            map(ShGoogleMapsService.mapGeocoderResultToMHLocation),
            catchError(error => of(null))
        );
    }

    searchByGeoLocation(geoLocation: string): Observable<MHLocation | null> {
        const trimmedGeoLocation: string = geoLocation.trim();
        if (trimmedGeoLocation.length < this.INPUT_MIN_CHARS) {
            return throwError(CustomErrorType.InvalidLatLngFormat);
        }

        return this.loadMapsAPI().pipe(
            map(() => this.buildGeoLocationRequest(trimmedGeoLocation)),
            switchMap(geocoderRequest => this.makeGeocoderRequest(geocoderRequest)),
            map(ShGoogleMapsService.mapGeocoderResultToMHLocation),
            catchError((error: Error) => throwError(error.message))
        );
    }

    private buildGeoLocationRequest(geoLocation: string): google.maps.GeocoderRequest {
        const latlngArray: string[] =
            geoLocation.indexOf(',') > 0
                ? geoLocation.split(',', 2)
                : geoLocation.split(' ').filter(value => value !== '');

        const lat: number = Number.parseFloat(latlngArray[0]);
        const lng: number = Number.parseFloat(latlngArray[1]);

        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            throw new Error(CustomErrorType.InvalidLatLngFormat);
        }

        const latLngLocation: google.maps.LatLng = new google.maps.LatLng(
            Number.parseFloat(latlngArray[0]),
            Number.parseFloat(latlngArray[1])
        );

        const geocoderRequest: google.maps.GeocoderRequest = {
            location: latLngLocation,
            bounds: this.belgiumBounds
        };

        return geocoderRequest;
    }

    makeGeocoderRequest(request: google.maps.GeocoderRequest): Observable<google.maps.GeocoderResult> {
        const self: this = this;

        return new Observable<google.maps.GeocoderResult>(observer => {
            const callbackfct: (
                results: google.maps.GeocoderResult[] | null,
                status: google.maps.GeocoderStatusString
            ) => void = (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatusString) => {
                self.ngZone.run(() => {
                    if (status !== google.maps.GeocoderStatus.OK) {
                        console.error(
                            'Google Maps service: geocode was not successful for the following reason: ' + status
                        );
                        observer.error(status);
                        return;
                    }
                    if (!results) {
                        console.error('Google Maps service: geocode was not successful');
                        return;
                    }
                    observer.next(results[0]);
                    observer.complete();
                });
            };
            this.geocoder.geocode(request, callbackfct);
        });
    }
}
